/**
 * The Diamond Decoder. A29.
 *
 * Three things move on this page and none of them is decoration:
 *
 *   02  Pick two stones. When two are chosen the answer opens and each choice
 *       is marked right or wrong. "Try again" clears it.
 *   03  Six numbered spots on the certificate and six rows beside it. Either
 *       opens the row; only one is open at a time.
 *   05  A diamond drawn to scale on a hand. The chips set the carat, the stone
 *       resizes, and "Use my own photo" swaps the hand for hers without
 *       sending a byte anywhere — an object URL, revoked when replaced.
 */

/* ------------------------------------------------------------------ 02 */

const game = document.querySelector('[data-game]');
if (game) {
  const stones = [...game.querySelectorAll('[data-stone]')];
  const prompt = game.querySelector('[data-game-prompt]');
  const answer = game.querySelector('[data-game-answer]');
  const label = game.querySelector('[data-game-label]');
  const need = Number(game.dataset.diamonds) || 2;

  const picked = () => stones.filter((s) => s.getAttribute('aria-pressed') === 'true');

  const settle = () => {
    const chosen = picked();
    const done = chosen.length >= need;

    if (prompt) {
      prompt.hidden = done;
      prompt.textContent = chosen.length === 0 ? prompt.dataset.first : prompt.dataset.more;
    }

    stones.forEach((stone) => {
      const mark = stone.querySelector('[data-stone-mark]');
      const on = stone.getAttribute('aria-pressed') === 'true';
      const right = stone.dataset.diamond === 'true';
      stone.classList.toggle('is-right', done && on && right);
      stone.classList.toggle('is-wrong', done && on && !right);
      if (mark) mark.textContent = done && on ? (right ? '✓' : '✕') : on ? '◇' : '';
    });

    if (answer) answer.hidden = !done;
    if (label && done) {
      const allRight = chosen.every((s) => s.dataset.diamond === 'true');
      label.textContent = allRight ? label.dataset.right : label.dataset.wrong;
    }
  };

  game.addEventListener('click', (event) => {
    const stone = event.target.closest('[data-stone]');
    if (stone) {
      const on = stone.getAttribute('aria-pressed') === 'true';
      if (!on && picked().length >= need) return;
      stone.setAttribute('aria-pressed', String(!on));
      settle();
      return;
    }
    if (event.target.closest('[data-game-reset]')) {
      stones.forEach((s) => s.setAttribute('aria-pressed', 'false'));
      settle();
    }
  });

  settle();
}

/* ------------------------------------------------------------------ 03 */

const cert = document.querySelector('[data-spots]');
if (cert) {
  const open = (no) => {
    cert.querySelectorAll('[data-spot]').forEach((node) => {
      const on = no !== null && node.dataset.spot === no;
      if (node.classList.contains('cert__dot')) node.setAttribute('aria-pressed', String(on));
      else {
        node.setAttribute('aria-expanded', String(on));
        const text = node.querySelector('.cert__row-text');
        if (text) text.hidden = !on;
      }
    });
  };

  cert.addEventListener('click', (event) => {
    const node = event.target.closest('[data-spot]');
    if (!node) return;
    const already =
      node.getAttribute('aria-pressed') === 'true' || node.getAttribute('aria-expanded') === 'true';
    open(already ? null : node.dataset.spot);
  });
}

/* ------------------------------------------------------------------ 05 */

const scale = document.querySelector('[data-scale]');
if (scale) {
  const stone = scale.querySelector('[data-stone-mark-size]');
  const caratLabel = scale.querySelector('[data-carat-label]');
  const mmLine = scale.querySelector('.hand__mm');
  const mmTemplate = mmLine ? mmLine.textContent : '';
  const chips = [...scale.querySelectorAll('[data-carat]')];
  // 5.6px per millimetre puts a 6.5mm one-carat stone at 36px inside a 520px
  // frame — the design's own ratio, so "true scale" is true at that width.
  const PX_PER_MM = 5.6;

  const set = (chip) => {
    const mm = parseFloat(chip.dataset.carat) || 0;
    chips.forEach((c) => c.setAttribute('aria-pressed', String(c === chip)));
    if (stone) stone.style.setProperty('--stone', `${Math.round(mm * PX_PER_MM)}px`);
    if (caratLabel) caratLabel.textContent = chip.textContent.trim();
    if (mmLine) mmLine.textContent = mmTemplate.replace('%mm%', `${mm.toFixed(1)}mm`);
  };

  scale.addEventListener('click', (event) => {
    const chip = event.target.closest('[data-carat]');
    if (chip) set(chip);
  });

  const own = scale.querySelector('[data-own-photo]');
  let objectUrl = null;
  own?.addEventListener('change', () => {
    const file = own.files && own.files[0];
    if (!file) return;
    const frame = scale.querySelector('[data-hand-frame]');
    if (!frame) return;
    // Her photo, on her device. Nothing is posted; the old URL is released.
    if (objectUrl) URL.revokeObjectURL(objectUrl);
    objectUrl = URL.createObjectURL(file);
    let img = frame.querySelector('img');
    if (!img) {
      const empty = frame.querySelector('.hand__image--empty');
      img = document.createElement('img');
      img.className = 'hand__image';
      img.alt = '';
      if (empty) empty.replaceWith(img);
      else frame.prepend(img);
    }
    img.src = objectUrl;
  });

  const initial = chips.find((c) => c.getAttribute('aria-pressed') === 'true') || chips[0];
  if (initial) set(initial);
}
